
import React, { ReactNode, useState, useEffect, useCallback } from 'react';

// DragDropContext
type DragDropContextProps = {
  onDragEnd: (result: any) => void;
  onDragStart?: (result: any) => void;
  children: ReactNode;
};

export const DragDropContext: React.FC<DragDropContextProps> = ({ onDragEnd, onDragStart, children }) => {
  // Create a context reference to store the callbacks
  const contextRef = React.useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Store the callbacks on the DOM element for access during drag events
    if (contextRef.current) {
      (contextRef.current as any).onDragEnd = onDragEnd;
      (contextRef.current as any).onDragStart = onDragStart;
      (contextRef.current as any).setAttribute('data-rbd-context', 'true');
    }
  }, [onDragEnd, onDragStart]);
  
  return <div ref={contextRef}>{children}</div>;
};

// Droppable
type DroppableProps = {
  droppableId: string;
  children: (provided: DroppableProvided) => ReactNode;
};

type DroppableProvided = {
  innerRef: (element: HTMLElement | null) => void;
  droppableProps: {
    'data-rbd-droppable-id': string;
    'data-rbd-droppable-context-id': string;
  };
  placeholder: ReactNode;
};

export const Droppable: React.FC<DroppableProps> = ({ droppableId, children }) => {
  const provided: DroppableProvided = {
    innerRef: (element) => {
      // Store reference to the droppable element
      if (element) {
        (element as any).__droppableId = droppableId;
      }
    },
    droppableProps: {
      'data-rbd-droppable-id': droppableId,
      'data-rbd-droppable-context-id': '1',
    },
    placeholder: null,
  };

  return <>{children(provided)}</>;
};

// Draggable
type DraggableProps = {
  draggableId: string;
  index: number;
  children: (provided: DraggableProvided, snapshot: DraggableStateSnapshot) => ReactNode;
};

type DraggableProvided = {
  innerRef: (element: HTMLElement | null) => void;
  draggableProps: {
    'data-rbd-draggable-id': string;
    'data-rbd-draggable-context-id': string;
    style?: React.CSSProperties;
  };
  dragHandleProps: {
    'data-rbd-drag-handle-draggable-id': string;
    'data-rbd-drag-handle-context-id': string;
    role: string;
    'aria-describedby': string;
    tabIndex: number;
    draggable: boolean;
    onDragStart: (e: React.DragEvent<HTMLElement>) => void;
    onDragEnd: (e: React.DragEvent<HTMLElement>) => void;
  };
};

type DraggableStateSnapshot = {
  isDragging: boolean;
  isDropAnimating: boolean;
  dropAnimation: null | {
    duration: number;
    curve: string;
    moveTo: {
      x: number;
      y: number;
    };
  };
  draggingOver: string | null;
  combineWith: string | null;
  combineTargetFor: string | null;
  mode: string | null;
};

export const Draggable: React.FC<DraggableProps> = ({ draggableId, index, children }) => {
  const [isDragging, setIsDragging] = React.useState(false);
  
  // Store current drag state
  const dragStateRef = React.useRef<{
    draggableId: string;
    sourceColumn: string | null;
    sourceIndex: number;
  }>({
    draggableId: draggableId,
    sourceColumn: null,
    sourceIndex: index
  });

  const handleDragStart = (e: React.DragEvent<HTMLElement>) => {
    setIsDragging(true);
    
    // Find parent droppable container
    let parent = e.currentTarget.parentElement;
    while (parent && !(parent as any).__droppableId) {
      parent = parent.parentElement;
    }
    
    if (parent) {
      dragStateRef.current.sourceColumn = (parent as any).__droppableId;
    }
    
    // Set drag data
    e.dataTransfer.setData("text/plain", JSON.stringify({
      draggableId,
      sourceColumn: dragStateRef.current.sourceColumn,
      sourceIndex: index
    }));
    
    // Find context to call onDragStart
    let context = document.querySelector('[data-rbd-context="true"]');
    if (context && (context as any).onDragStart) {
      (context as any).onDragStart({
        draggableId,
        source: {
          droppableId: dragStateRef.current.sourceColumn,
          index
        }
      });
    }
    
    // Customize drag image appearance
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = (e: React.DragEvent<HTMLElement>) => {
    setIsDragging(false);
    
    // Find target droppable
    let targetElement = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement;
    if (!targetElement) return;
    
    // Find the closest droppable container
    while (targetElement && !(targetElement as any).__droppableId) {
      targetElement = targetElement.parentElement as HTMLElement;
      if (!targetElement) return; // Exit if we can't find a droppable
    }
    
    if (targetElement) {
      const targetColumn = (targetElement as any).__droppableId;
      
      // Only process if we have source and destination
      if (dragStateRef.current.sourceColumn && targetColumn) {
        // Get closest context to get onDragEnd handler
        let context = document.querySelector('[data-rbd-context="true"]');
        
        if (context && (context as any).onDragEnd) {
          // Get exact drop position within the target column
          const targetCards = Array.from(targetElement.querySelectorAll('[data-rbd-draggable-id]'));
          let destinationIndex = targetCards.length; // Default to end of list
          
          // Get mouse position relative to target element
          const mouseY = e.clientY;
          
          // Find closest card based on mouse position
          for (let i = 0; i < targetCards.length; i++) {
            const card = targetCards[i];
            const rect = card.getBoundingClientRect();
            const cardMiddle = rect.top + rect.height / 2;
            
            if (mouseY < cardMiddle) {
              destinationIndex = i;
              break;
            }
          }
          
          // Call the onDragEnd handler from context with improved timing
          // Use requestAnimationFrame for smoother updates
          requestAnimationFrame(() => {
            // Ensure we immediately call onDragEnd to update the data
            const result = {
              source: {
                droppableId: dragStateRef.current.sourceColumn,
                index: dragStateRef.current.sourceIndex
              },
              destination: {
                droppableId: targetColumn,
                index: destinationIndex
              },
              draggableId
            };
            
            // Log the drag result for debugging
            console.log("Drag ended:", result);
            
            // Call the provided onDragEnd handler
            (context as any).onDragEnd(result);
          });
        }
      }
    }
  };

  const provided: DraggableProvided = {
    innerRef: (element) => {
      // Store reference to draggable element
      if (element) {
        (element as any).draggableId = draggableId;
        (element as any).draggableIndex = index;
      }
    },
    draggableProps: {
      'data-rbd-draggable-id': draggableId,
      'data-rbd-draggable-context-id': '1',
      style: isDragging ? { opacity: 0.5 } : undefined
    },
    dragHandleProps: {
      'data-rbd-drag-handle-draggable-id': draggableId,
      'data-rbd-drag-handle-context-id': '1',
      role: 'button',
      'aria-describedby': `draggable-${draggableId}`,
      tabIndex: 0,
      draggable: true,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd
    },
  };

  const snapshot: DraggableStateSnapshot = {
    isDragging,
    isDropAnimating: false,
    dropAnimation: null,
    draggingOver: null,
    combineWith: null,
    combineTargetFor: null,
    mode: null,
  };

  return <>{children(provided, snapshot)}</>;
};
